import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useCallOnce from "../hooks/useCallOnce";
import {
  getDefaultPrompt,
  changeDefaultAgentPrompt,
} from "../redux/actions/defaultPromptActions";
import {
  getDefaultVoicePrompt,
} from "../redux/actions/defaultVoicePromptActions";
import { getUsersListForDropdown } from "../redux/actions/userActions";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AllPrompt() {
  const dispatch = useDispatch();
  const { prompt, save } = useSelector((s) => s.defaultPrompt);
  const defaultVoicePrompt = useSelector((s) => s.defaultVoicePrompt);
  const { dropdown } = useSelector((state) => state.users);

  const [activeTab, setActiveTab] = useState(0);
  const [promptText, setPromptText] = useState("");
  const [voicePromptText, setVoicePromptText] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);

  // Fetch default prompt on component mount
  useCallOnce(
    "allprompt/init",
    () => {
      dispatch(getDefaultPrompt());
    },
    [dispatch]
  );

  // Fetch voice prompt when voice tab is clicked
  const [voicePromptFetched, setVoicePromptFetched] = useState(false);
  useEffect(() => {
    if (activeTab === 1 && !voicePromptFetched) {
      dispatch(getDefaultVoicePrompt());
      setVoicePromptFetched(true);
    }
  }, [activeTab, voicePromptFetched, dispatch]);

  // Fetch users dropdown for brand selection
  useCallOnce(
    "allprompt/dropdown",
    () => {
      dispatch(getUsersListForDropdown("", 1, 100));
    },
    [dispatch]
  );

  // Update simple prompt text when data is fetched
  useEffect(() => {
    const payload = prompt?.data?.data || prompt?.data || {};
    const existingPrompt = payload?.prompt || "";
    setPromptText(existingPrompt);
  }, [prompt]);

  // Update voice prompt text when data is fetched
  useEffect(() => {
    const payload = defaultVoicePrompt?.data?.data || defaultVoicePrompt?.data || {};
    const existingPrompt = payload?.prompt || "";
    setVoicePromptText(existingPrompt);
  }, [defaultVoicePrompt]);

  const loadingSimplePrompt = prompt.loading;
  const loadingVoicePrompt = defaultVoicePrompt.loading;
  const saving = save.loading;

  // Check if text field has content
  const hasSimpleContent = useMemo(() => {
    return promptText.trim().length > 0;
  }, [promptText]);

  const hasVoiceContent = useMemo(() => {
    return voicePromptText.trim().length > 0;
  }, [voicePromptText]);

  const handleSave = () => {
    setConfirmDialogOpen(true);
    setConfirmationText("");
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    const userIds = selectedBrands.map(
      (brand) => brand._id || brand.userId || brand.id
    );
    
    if (activeTab === 0) {
      // Simple Prompt
      await dispatch(changeDefaultAgentPrompt(promptText, userIds, "simpleprompt"));
    } else {
      // Voice Prompt
      await dispatch(changeDefaultAgentPrompt(voicePromptText, userIds, "voiceprompt"));
    }
    setConfirmationText("");
  };

  const handleCancelSave = () => {
    setConfirmDialogOpen(false);
    setConfirmationText("");
  };

  const isConfirmDisabled = confirmationText !== "I'll confirm this";

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const currentLoading = activeTab === 0 ? loadingSimplePrompt : loadingVoicePrompt;
  const currentHasContent = activeTab === 0 ? hasSimpleContent : hasVoiceContent;

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Default Agent Prompt Settings
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2, border: "1px solid #e5e7eb" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="prompt tabs"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "15px",
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Simple Prompt" />
            <Tab label="Voice Prompt" />
          </Tabs>
        </Paper>

        {/* Brand Selection Dropdown */}
        <Paper sx={{ p: 2, border: "1px solid #e5e7eb", mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Select Brand
          </Typography>
          <Autocomplete
            multiple
            size="small"
            fullWidth
            options={dropdown.users || []}
            loading={Boolean(dropdown?.loading)}
            loadingText="Loading brands..."
            value={selectedBrands}
            getOptionLabel={(u) =>
              u?.company ||
              u?.email ||
              u?.name ||
              u?._id ||
              u?.userId ||
              u?.id ||
              ""
            }
            isOptionEqualToValue={(a, b) =>
              (a?._id || a?.userId || a?.id) === (b?._id || b?.userId || b?.id)
            }
            onChange={(e, value) => {
              setSelectedBrands(value || []);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={
                    option?.company || option?.email || option?.name || "Brand"
                  }
                  {...getTagProps({ index })}
                  size="small"
                  key={index}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select brands to apply this prompt"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {dropdown?.loading ? (
                        <CircularProgress
                          color="inherit"
                          size={16}
                          sx={{ mr: 1 }}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", mt: 0.5, display: "block" }}
          >
            Leave empty to apply to all brands, or select specific brands to
            apply this prompt.
          </Typography>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {/* Simple Prompt Tab */}
          <Paper sx={{ p: 2, border: "1px solid #e5e7eb" }}>
            <Box sx={{ position: "relative" }}>
              {loadingSimplePrompt && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.6)",
                    zIndex: 2,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={20}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="Enter default prompt settings here..."
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "monospace",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !hasSimpleContent}
                sx={{ textTransform: "none" }}
              >
                {saving ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Voice Prompt Tab */}
          <Paper sx={{ p: 2, border: "1px solid #e5e7eb" }}>
            <Box sx={{ position: "relative" }}>
              {loadingVoicePrompt && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.6)",
                    zIndex: 2,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={20}
                value={voicePromptText}
                onChange={(e) => setVoicePromptText(e.target.value)}
                placeholder="Enter voice prompt settings here..."
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "monospace",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !hasVoiceContent}
                sx={{ textTransform: "none" }}
              >
                {saving ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        {/* Confirmation Dialog with Text Input */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelSave}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Save</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to save these {activeTab === 0 ? "simple" : "voice"} prompt settings? This
              will update the default agent prompt configuration for all agents.
            </DialogContentText>
            <DialogContentText sx={{ mb: 2, fontWeight: 600 }}>
              Please type "I'll confirm this" below to enable the confirm
              button:
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              placeholder="Type: I'll confirm this"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              disabled={saving}
              variant="outlined"
              size="small"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelSave} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              variant="contained"
              disabled={saving || isConfirmDisabled}
              sx={{ textTransform: "none" }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Confirm Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}